import { useEffect } from "react";
import { isTextEntryTarget } from "./ChatPage.utils";
import type { ChatKeyboardShortcutsContext } from "./ChatContextTypes";

export function useChatKeyboardShortcuts(
  context: ChatKeyboardShortcutsContext,
) {
  const {
    conversationRef,
    lastEscapePress,
    regenerate,
    sending,
    stopGeneration,
  } = context;
  useEffect(() => {
    if (!sending) {
      lastEscapePress.current = 0;
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.isComposing) return;

      const now = Date.now();
      if (now - lastEscapePress.current <= 450) {
        lastEscapePress.current = 0;
        event.preventDefault();
        event.stopImmediatePropagation();
        stopGeneration();
        return;
      }

      lastEscapePress.current = now;
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [lastEscapePress, sending, stopGeneration]);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.code !== "KeyK") return;

      const composer = document.querySelector<HTMLElement>(
        '[data-chat-composer="true"] [contenteditable="true"]',
      );
      if (!composer) return;

      event.preventDefault();
      composer.focus();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey) return;
      if (event.defaultPrevented) return;
      // Save belongs to the workbench even while an agent is generating.
      if (
        event.code === "KeyS" &&
        document.querySelector("[data-workspace-editor]")
      )
        return;

      if (event.code === "KeyS" && !event.shiftKey && sending) {
        event.preventDefault();
        event.stopImmediatePropagation();
        stopGeneration();
        return;
      }
      if (event.shiftKey && isTextEntryTarget(event.target)) return;

      const latestAssistant = [...(conversationRef.current?.messages ?? [])]
        .reverse()
        .find((message) => message.role === "assistant");
      if (!latestAssistant) return;

      if (event.code === "KeyC" && event.shiftKey) {
        if (!latestAssistant.content || !navigator.clipboard) return;
        event.preventDefault();
        void navigator.clipboard.writeText(latestAssistant.content);
        return;
      }
      if (event.code === "KeyR" && event.shiftKey && !sending) {
        event.preventDefault();
        void regenerate(latestAssistant);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [conversationRef, regenerate, sending, stopGeneration]);
}
